/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable perfectionist/sort-imports */
import 'src/global.css';

// ----------------------------------------------------------------------

import { lazy, Suspense, useState, useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useLocation } from 'react-router';

import ThemeProvider from 'src/theme';
import { LocalizationProvider } from 'src/locales';

import { SettingsProvider } from 'src/components/settings';

import i18n from './i18n';
import { getRuntimeConfig, getDynamicApiHost } from './utils/configUtil/configUtil';
import { SnackbarProvider } from './components/snackbar';
import ModalProvider from './provider/modal-provider';

import useInitApp from './hooks/use-init-app';
import { updateAxiosBaseURL } from './api/instance';
import useRouteHandler from './hooks/use-route-handler';
import localStorageUtil from './utils/localStorageUtil';
import { loginTypeConstants } from './utils/constants/loginConstants';
import { updateLocalStorage, updateBrowserTitle } from './utils/app/update-app';
import { SplashScreen } from './components/loading-screen';

import useRouteTracker from './hooks/use-route-tracker';
import ErrorBoundaryWrapper from './components/error-boundary';
import useRedirectPath from './hooks/use-redirect-path';
import useBackNavigationGuard from './hooks/use-back-navigation-guard';
import useStandardLogin from './hooks/use-standard-login';
import useI18nInitializer from './hooks/use-i18n-initializer';
import { useGlobalSecurity } from './hooks/use-global-security';
import usePushBridge from './hooks/use-push-bridge';

const Router = lazy(() => import('src/routes/sections'));
const ProgressBar = lazy(() => import('src/components/progress-bar'));
const MotionLazy = lazy(() =>
  import('src/components/animate/motion-lazy').then((module) => ({
    default: module.MotionLazy,
  }))
);
const SettingsDrawer = lazy(() =>
  import('src/components/settings').then((module) => ({
    default: module.SettingsDrawer,
  }))
);
const NotFoundPage = lazy(() => import('./pages/404'));

// ---------------------------------Step 1 : 최초 Standard 로직 검사 App 컴포넌트-------------------------------------

export default function App() {
  const runtimeConfig = getRuntimeConfig();
  updateAxiosBaseURL(runtimeConfig?.VUE_APP_API_HOST ?? '');
  const standardLoginHook = useStandardLogin();
  const isStandardLoginReady = standardLoginHook.isReady;

  const { pathname } = window.location;
  const isFallback404 = ['/404', '/404.html'].includes(pathname);

  if (!runtimeConfig) {
    console.log('Runtime Config 없음');
    if (!isFallback404) {
      window.location.href = '/404';
    }
    return (
      <Suspense fallback={<SplashScreen />}>
        <NotFoundPage />
      </Suspense>
    );
  }

  if (!isStandardLoginReady) {
    return <SplashScreen />;
  }

  return <AfterCheckServiceTypeApp />;
}

// ---------------------------------Step 2 : Standard 로직 검사 후 App 컴포넌트-------------------------------------

const AfterCheckServiceTypeApp = () => {
  /**
   * 앱 초기화 훅
   */
  useInitApp(); // 브랜드/멤버 정보 초기화
  useBackNavigationGuard(); // 뒤로가기 가드
  useRedirectPath(); // 리다이렉트 처리
  useRouteHandler(); // 라우트 핸들러
  useRouteTracker(); // 라우트 추적
  useGlobalSecurity(); // 보안 설정
  usePushBridge(); // FCM 토큰 비동기 수신 (네이티브 브릿지 : iOS 푸시토큰 비동기 수신)

  const brandCode = localStorageUtil.getBrandCode();

  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const totalLoginQuery = query.get(loginTypeConstants.TOTAL_LOGIN);
  if (totalLoginQuery) {
    localStorageUtil.setLoginType(loginTypeConstants.TOTAL_LOGIN, totalLoginQuery);
  }

  const { pathname } = window.location;
  const isFallback404 = ['/404', '/404.html'].includes(pathname);
  const [isConfigSetting, setIsConfigSetting] = useState<boolean>(false);

  // 앱 초기화 로직은 별도의 useEffect로 분리
  useEffect(() => {
    const runtimeConfig = getRuntimeConfig();

    if (!runtimeConfig && !isFallback404) {
      console.warn('Runtime Config 없음');
      window.location.href = '/404';
      return;
    }

    if (runtimeConfig) {
      // 로컬/개발 환경에서만 동적 엔드포인트 사용, 운영환경에서는 기존 로직 유지
      const apiHost = getDynamicApiHost(runtimeConfig);
      updateAxiosBaseURL(apiHost);
      updateLocalStorage(runtimeConfig);
      import('firebase/app')
        .then(({ initializeApp }) => {
          initializeApp(runtimeConfig.VUE_FIREBASE_INIT);
        })
        .catch(() => undefined);
      updateBrowserTitle();
      setIsConfigSetting(true);
    }
  }, []); // 앱 초기화는 한 번만 실행

  // 다국어코드 설정 커스텀 훅 실행
  // 엔터프라이즈/스탠다드 구분하여 i18n 초기화
  const { ready: isTranslationsReady } = useI18nInitializer(brandCode);

  if (!isConfigSetting) {
    return isFallback404 ? (
      <Suspense fallback={<SplashScreen />}>
        <NotFoundPage />
      </Suspense>
    ) : (
      <SplashScreen />
    );
  }

  if (!isTranslationsReady) return <SplashScreen />;

  return (
    <ErrorBoundaryWrapper>
      <I18nextProvider i18n={i18n}>
        <LocalizationProvider>
          <SettingsProvider
            defaultSettings={{
              themeMode: 'light',
              themeDirection: 'ltr',
              themeColorPresets: 'default',
            }}
          >
            <ThemeProvider>
              <SnackbarProvider>
                <ModalProvider>
                  <MotionLazy>
                    <ProgressBar />
                    <SettingsDrawer />
                    <Router />
                  </MotionLazy>
                </ModalProvider>
              </SnackbarProvider>
            </ThemeProvider>
          </SettingsProvider>
        </LocalizationProvider>
      </I18nextProvider>
    </ErrorBoundaryWrapper>
  );
};
